// Dictionnaire de traductions courantes pour les catégories
const commonTranslations: { [key: string]: { en: string; ln: string; sw: string } } = {
  // Mots courants
  'auto': { en: 'Auto', ln: 'Mituka', sw: 'Magari' },
  'moto': { en: 'Motorcycle', ln: 'Moto', sw: 'Pikipiki' },
  'électronique': { en: 'Electronics', ln: 'Elektronike', sw: 'Elektroniki' },
  'multimédia': { en: 'Multimedia', ln: 'Multimédia', sw: 'Multimédia' },
  'beauté': { en: 'Beauty', ln: 'Kitoko', sw: 'Uzuri' },
  'soins': { en: 'Care', ln: 'Bobateli', sw: 'Huduma' },
  'boissons': { en: 'Drinks', ln: 'Masanga', sw: 'Vinywaji' },
  'alimentation': { en: 'Food', ln: 'Bilei', sw: 'Chakula' },
  'enfants': { en: 'Kids', ln: 'Bana', sw: 'Watoto' },
  'bébés': { en: 'Babies', ln: 'Babebe', sw: 'Watoto Wachanga' },
  'jeux': { en: 'Games', ln: 'Masano', sw: 'Michezo' },
  'jouets': { en: 'Toys', ln: 'Biloko ya kobanga', sw: 'Vitu vya kuchezea' },
  'livres': { en: 'Books', ln: 'Mikanda', sw: 'Vitabu' },
  'culture': { en: 'Culture', ln: 'Mimeseno', sw: 'Utamaduni' },
  'éducation': { en: 'Education', ln: 'Boyekoli', sw: 'Elimu' },
  'maison': { en: 'Home', ln: 'Ndako', sw: 'Nyumba' },
  'jardin': { en: 'Garden', ln: 'Elanga', sw: 'Bustani' },
  'équipements': { en: 'Equipment', ln: 'Biloko', sw: 'Vifaa' },
  'vêtements': { en: 'Clothing', ln: 'Bilamba', sw: 'Nguo' },
  'accessoires': { en: 'Accessories', ln: 'Biloko', sw: 'Vifaa' },
  'mode': { en: 'Fashion', ln: 'Mode', sw: 'Mtindo' },
  'santé': { en: 'Health', ln: 'Bokolongono', sw: 'Afya' },
  'bien-être': { en: 'Wellness', ln: 'Bomoi Malamu', sw: 'Ustawi' },
  'sports': { en: 'Sports', ln: 'Masano', sw: 'Michezo' },
  'loisirs': { en: 'Leisure', ln: 'Bisengo', sw: 'Starehe' },
  'art': { en: 'Arts', ln: 'Masano', sw: 'Sanaa' },
};

// Symboles et mots de liaison
const connectors: { [key: string]: string } = {
  '&': 'and',
  'et': 'and',
  'de': 'of',
  'du': 'of',
  'des': 'of',
  'la': 'the',
  'le': 'the',
  'les': 'the',
};

/**
 * Traduit automatiquement un nom de catégorie français vers EN/LN/SW
 * @param frenchName Nom en français (ex: "Auto & Moto", "Électronique & Multimédia")
 * @returns Objet avec traductions { en, ln, sw }
 */
export const autoTranslateCategoryName = (frenchName: string): { en: string; ln: string; sw: string } => {
  if (!frenchName || frenchName.trim() === '') {
    return { en: '', ln: '', sw: '' };
  }

  // Normaliser le texte (enlever accents pour la recherche, mais garder l'original pour affichage)
  const normalized = frenchName.toLowerCase().trim();
  
  // Diviser en mots/tokens
  const tokens = normalized.split(/[\s&]+/);
  
  const enWords: string[] = [];
  const lnWords: string[] = [];
  const swWords: string[] = [];
  
  tokens.forEach((token) => {
    // Nettoyer le token
    const cleanToken = token.replace(/[.,;:!?]/g, '');
    
    if (cleanToken === '') return;
    
    // Chercher dans le dictionnaire (gérer les accents)
    const normalizedToken = removeAccents(cleanToken);
    const found = Object.keys(commonTranslations).find(
      key => removeAccents(key) === normalizedToken
    );
    
    if (found) {
      const translation = commonTranslations[found];
      enWords.push(translation.en);
      lnWords.push(translation.ln);
      swWords.push(translation.sw);
    } else if (connectors[cleanToken]) {
      // Gérer les connecteurs (& / et)
      enWords.push('&');
      lnWords.push('&');
      swWords.push('&');
    } else {
      // Pas trouvé: capitaliser la première lettre et utiliser tel quel
      const capitalized = cleanToken.charAt(0).toUpperCase() + cleanToken.slice(1);
      enWords.push(capitalized);
      lnWords.push(capitalized);
      swWords.push(capitalized);
    }
  });
  
  return {
    en: enWords.join(' '),
    ln: lnWords.join(' '),
    sw: swWords.join(' '),
  };
};

/**
 * Retire les accents d'une chaîne pour la comparaison
 */
const removeAccents = (str: string): string => {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

/**
 * Valide si une traduction semble correcte (pas juste une copie du français)
 */
export const isValidTranslation = (original: string, translation: string): boolean => {
  if (!translation || translation.trim() === '') return false;
  
  // Vérifier que ce n'est pas exactement la même chose
  const normalizedOriginal = removeAccents(original.toLowerCase());
  const normalizedTranslation = removeAccents(translation.toLowerCase());
  
  return normalizedOriginal !== normalizedTranslation;
};

/**
 * Génère automatiquement toutes les traductions manquantes
 * @param name Nom principal (utilisé comme FR)
 * @param currentTranslations Traductions existantes
 * @returns Traductions complètes
 */
export const generateMissingTranslations = (
  name: string,
  currentTranslations: { fr?: string; en?: string; ln?: string; sw?: string } | null
): { fr: string; en: string; ln: string; sw: string } => {
  const frenchName = currentTranslations?.fr || name || '';
  const auto = autoTranslateCategoryName(frenchName);
  
  return {
    fr: currentTranslations?.fr || frenchName,
    en: currentTranslations?.en || auto.en,
    ln: currentTranslations?.ln || auto.ln,
    sw: currentTranslations?.sw || auto.sw,
  };
};
